import i18next from 'i18next';
import forEach from "ramda/es/forEach";
import keys from "ramda/es/keys";
import isFunction from "ramda-extension/es/isFunction";
import format from '../format';
import resources from '../resources';
export let i18n;
const DEFAULT_LANGUAGE = 'cs';
export function getOrInitDefault() {
    if (!i18n) {
        initDefaultMessages();
    }
    return i18n;
}
function init() {
    i18n?.init(format);
}
/**
 * Initializes the i18next instance.
 *
 * @documented
 * @category localization
 * @param {Object} options - Please read the options page at https://www.i18next.com for details.
 * @return {undefined} nothing
 *
 */ export function initMessages(options) {
    i18n = i18next.createInstance(options);
    init();
}
/**
 * Initializes the i18next instance for given language (default value 'cs').
 *
 * @documented
 * @category localization
 * @param {String} lng = cs - language
 * @return {undefined} nothing
 *
 */ export function initDefaultMessages(lng = DEFAULT_LANGUAGE) {
    initMessages({
        lng,
        fallbackLng: lng
    });
    loadMessageResources(resources);
}
/**
 * Main function for translation.
 *
 * Please read the translate pages at https://www.i18next.com for details.
 *
 * @documented
 * @category localization
 * @param {String} key - language
 * @param {Object} [options] - options
 * @param {...Args} [...args] - args for the function
 * @return {String} message
 *
 * @example
 * import { getMessage } from '@gef-ui/localization/messages'
 *
 * getMessage('person:surname'); // output: příjmení
 *
 */ export function getMessage(key, options, ...args) {
    const message = getOrInitDefault().t(key, options);
    if (isFunction(message)) {
        return message(...args);
    } else {
        return message.replace(/&#x2F;/g, '/');
    }
}
/**
 * All common messages are labeled (capitalized).
 * You can access them using context 'label'. Or you can use this function, that sets context for you.
 *
 * Please read the translate pages at https://www.i18next.com for details.
 *
 * @documented
 * @category localization
 * @param {String} key - language
 * @param {Object} [options] - options
 * @param {...Args} [...args] - args for the function
 * @return {String} message
 *
 * @example
 * import { getLabeledMessage } from '@gef-ui/localization/messages'
 *
 * getLabeledMessage('person:surname'); // output: Příjmení
 *
 */ export function getLabelMessage(key, options, ...args) {
    return getMessage(key, {
        ...options,
        context: 'label'
    }, args);
}
/**
 * All validation messages should be in namespace 'validation'. You can use this function, that sets namespace for you.
 *
 * Please read the translate pages at https://www.i18next.com for details.
 *
 * @documented
 * @category localization
 * @param {String} key - language
 * @param {Object} [options] - options
 * @param {...Args} [...args] - args for the function
 * @return {String} message
 *
 * @example
 * import { getValidationMessage } from '@gef-ui/localization/messages'
 *
 * getValidationMessage('requiredField', { field: 'Some field' }); // output: Pole Some field je povinné.
 * getValidationMessage('requiredField', { field: 'person:surname' }); // output: Pole příjmení je povinné.
 *
 */ export function getValidationMessage(key, options, ...args) {
    return getMessage(key, {
        ...options,
        ns: 'validation'
    }, ...args);
}
/**
 * Adds a complete bundle of messages to given language and namespace.
 * It extends existing bundle deep and overwrites existing values.
 *
 * @documented
 * @category localization
 * @param {Object} resources - resources
 * @param {String} lng = cs - language
 * @param {String} ns = translation - namespace
 * @return {undefined} nothing
 *
 */ export function addResourceBundle(resources, language = DEFAULT_LANGUAGE, ns = 'translation') {
    getOrInitDefault().addResourceBundle(language, ns, resources, true, true);
}
/**
 * Get boolean label
 *
 * @documented
 * @category localization
 * @param {Any} value - value is resolved using thruty/falsy check
 * @return {String} message
 *
 * @example
 * import { getBooleanLabel } from '@gef-ui/localization/messages'
 *
 * getBooleanLabel(0); // Ne
 * getBooleanLabel(1); // Ano
 */ export const getBooleanLabel = (value)=>getLabelMessage(value ? 'common:yes' : 'common:no');
/**
 *load resources from object with all languages and namespaces.
 *Internal implementation uses addResourceBundle so you can overwrite default messages.
 *
 * @documented
 * @category localization
 * @param {Object} resources - resources
 * @return {undefined} nothing
 *
 */ export function loadMessageResources(resources) {
    forEach((language)=>forEach((namespace)=>addResourceBundle(resources[language][namespace], language, namespace), keys(resources[language])), keys(resources));
}
export default {
    getOrInitDefault,
    i18n,
    initMessages,
    initDefaultMessages,
    addResourceBundle,
    getMessage,
    getLabelMessage,
    getValidationMessage,
    loadMessageResources
};
